import focusBearApi from 'services/axios-config';
import Endpoints from 'constants/endpoints';
import {
  SUBSCRIPTION_PLAN,
  SUBSCRIPTION_PRICE,
  SUBSCRIPTION_PRICE_ID
} from 'constants/billingInfo';
import { ENTITLEMENT, TEAM_ACCESS_ROLE } from 'enums';
import { SINGLE_ENTITLEMENT } from 'constants/general';
import { t } from 'services/i18n.service';
import { Entitlement } from 'interfaces/initial-states.interface';
import { Team } from 'interfaces/team.interface';
import { getDaysUntilDate, isFutureDateTime, isNowOrAfter } from './time.util';
import {
  ActiveEntitlement,
  Subscription
} from 'interfaces/subscription.interface';
import { isTeamSubscriptionPlan } from './helpers';
import { getSubscriptionTerminology } from './team.util';

export const SUBSCRIPTION_HISTORY_PREVIEW_LIMIT = 2;

export type SubscriptionStatusView = 'active' | 'scheduled_to_end' | 'inactive';

export const SUBSCRIPTION_STATUS_VIEW = {
  ACTIVE: 'active',
  SCHEDULED_TO_END: 'scheduled_to_end',
  INACTIVE: 'inactive'
} as const satisfies Record<string, SubscriptionStatusView>;

export interface SubscriptionViewSections {
  currentSubscriptions: Subscription[];
  historySubscriptions: Subscription[];
}

export type SubscriptionRoleBadgeId =
  | 'owner'
  | 'admin'
  | 'teacher'
  | 'member'
  | 'student';

export interface SubscriptionRoleBadge {
  id: SubscriptionRoleBadgeId;
  labelKey: string;
  color: string;
}

export type SubscriptionTeam = Pick<
  Team,
  | 'id'
  | 'name'
  | 'owner_id'
  | 'expires_date'
  | 'is_class'
  | 'is_school'
  | 'access_role'
>;

export interface SubscriptionListItem {
  id: string;
  subscription: Subscription;
  roleBadges: SubscriptionRoleBadge[];
  isTeamPlan: boolean;
  /** Team this card represents; null for personal plans and the merged fallback. */
  teamName: string | null;
  /** Team licence expiry, which overrides the shared subscription expiry. */
  teamExpiryDate: string | null;
  isSchoolTeam: boolean;
}

const TEAM_ACCESS_ENTITLEMENTS: ENTITLEMENT[] = [
  ENTITLEMENT.TEAM_OWNER,
  ENTITLEMENT.TEAM_ADMIN,
  ENTITLEMENT.TEAM_MEMBER
];

const ROLE_BADGE_STYLES: Record<
  SubscriptionRoleBadgeId,
  Omit<SubscriptionRoleBadge, 'id'>
> = {
  owner: {
    labelKey: 'subscription_page.owner',
    color: 'bg-primary-500'
  },
  admin: {
    labelKey: 'subscription_page.admin',
    color: 'bg-red-600'
  },
  teacher: {
    labelKey: 'subscription_page.teacher',
    color: 'bg-amber-600'
  },
  member: {
    labelKey: 'subscription_page.member',
    color: 'bg-blue-600'
  },
  student: {
    labelKey: 'school.student',
    color: 'bg-blue-600'
  }
};

export const getStripeCheckoutURL = async (
  price_id: string,
  team_name?: string,
  options?: { is_school?: boolean }
) => {
  const response = await focusBearApi.post(
    Endpoints.STRIPE.CREATE_CHECKOUT_SESSION,
    {
      price_id,
      team_name,
      ...(options?.is_school !== undefined
        ? { is_school: options.is_school }
        : {})
    }
  );
  window.location.href = response.data.url;
};

export const getSubscriptionManagementPortal = async () => {
  const response = await focusBearApi.post(
    Endpoints.STRIPE.CREATE_PORTAL_SESSION
  );
  window.location.href = response.data.url;
};

export const isTrialOrPromoActive = (entitlements: Entitlement[]) =>
  entitlements.length > 0 &&
  Object.values(entitlements ?? []).every(
    (entitlement) =>
      (entitlement.product_identifier ?? '').includes('trial') ||
      (entitlement.product_identifier ?? '').includes('promo')
  );

const isTrialOrPromoIdentifier = (productIdentifier?: string | null): boolean =>
  (productIdentifier ?? '').includes('trial') ||
  (productIdentifier ?? '').includes('promo');

export const isActiveSubscription = (subscription: Subscription): boolean =>
  subscription.expires_date === null ||
  isNowOrAfter(subscription.expires_date, 'second');

export const isSubscriptionPlanActive = ({
  subscriptions,
  planIdentifier
}: {
  subscriptions: Subscription[];
  planIdentifier: string;
}): boolean =>
  subscriptions.some((subscription) => {
    const hasMatchingProductIdentifier =
      subscription.product_identifier === planIdentifier;
    const isCanceled = Boolean(subscription.unsubscribe_detected_at);

    return (
      hasMatchingProductIdentifier &&
      !isCanceled &&
      isActiveSubscription(subscription)
    );
  });

const sortSubscriptionsByLatest = (subscriptions: Subscription[]) =>
  [...subscriptions].sort((a, b) => {
    const aDate = new Date(
      a.purchase_date ?? a.original_purchase_date ?? a.expires_date ?? 0
    ).getTime();
    const bDate = new Date(
      b.purchase_date ?? b.original_purchase_date ?? b.expires_date ?? 0
    ).getTime();
    return bDate - aDate;
  });

export const getSubscriptionStatusView = (
  subscription: Subscription
): SubscriptionStatusView => {
  if (isActiveSubscription(subscription)) {
    return subscription.unsubscribe_detected_at
      ? SUBSCRIPTION_STATUS_VIEW.SCHEDULED_TO_END
      : SUBSCRIPTION_STATUS_VIEW.ACTIVE;
  }

  return SUBSCRIPTION_STATUS_VIEW.INACTIVE;
};

/**
 * When user has an active paid subscription, hide inactive/expired trial-promo
 * items from the list. If user has no active paid subscription, keep trial-promo
 * items visible to show their inactive status/history.
 */
export const filterSubscriptionListByTrialPromoState = (
  subscriptions: Subscription[]
): Subscription[] => {
  const nonTrialPromoSubscriptions = subscriptions.filter(
    ({ product_identifier }) => !isTrialOrPromoIdentifier(product_identifier)
  );

  const hasActivePaidSubscription =
    nonTrialPromoSubscriptions.some(isActiveSubscription);

  if (!hasActivePaidSubscription) {
    return subscriptions;
  }

  return subscriptions.filter((subscription) => {
    const isTrialOrPromoSubscription = isTrialOrPromoIdentifier(
      subscription.product_identifier
    );
    return !isTrialOrPromoSubscription || isActiveSubscription(subscription);
  });
};

export const getSubscriptionViewSections = (
  subscriptions: Subscription[]
): SubscriptionViewSections => {
  const filteredSubscriptions =
    filterSubscriptionListByTrialPromoState(subscriptions);
  const sortedSubscriptions = sortSubscriptionsByLatest(filteredSubscriptions);

  const currentSubscriptions = sortedSubscriptions.filter((subscription) =>
    ['active', 'scheduled_to_end'].includes(
      getSubscriptionStatusView(subscription)
    )
  );
  const historySubscriptions = sortedSubscriptions.filter(
    (subscription) =>
      getSubscriptionStatusView(subscription) ===
      SUBSCRIPTION_STATUS_VIEW.INACTIVE
  );

  return {
    currentSubscriptions,
    historySubscriptions
  };
};

/**
 * Returns true when the user has a team entitlement (member, admin, or owner).
 * Team members should NOT see personal-plan upsells — their access is managed
 * by the team owner's subscription, and rc_promo_team_* identifiers contain
 * 'promo' which would otherwise trigger isTrialOrPromoActive incorrectly.
 */
export const isTeamMemberUser = (entitlements: Entitlement[]): boolean =>
  Object.values(entitlements ?? []).some((entitlement) =>
    [
      ENTITLEMENT.TEAM_MEMBER,
      ENTITLEMENT.TEAM_ADMIN,
      ENTITLEMENT.TEAM_OWNER
    ].includes(entitlement.entitlement)
  );

const createRoleBadge = (
  id: SubscriptionRoleBadgeId
): SubscriptionRoleBadge => ({
  id,
  ...ROLE_BADGE_STYLES[id]
});

const getNonClassTeams = <T extends Pick<Team, 'is_class'>>(teams: T[]): T[] =>
  teams.filter((team) => team.is_class !== true);

interface TeamAccessGroup {
  team: SubscriptionTeam;
  accessRoles: Set<TEAM_ACCESS_ROLE>;
}

/** Merges duplicate rows for the same team so admin + teacher share one card. */
const groupTeamsById = (teams: SubscriptionTeam[]): TeamAccessGroup[] => {
  const groups = new Map<string, TeamAccessGroup>();
  getNonClassTeams(teams).forEach((team) => {
    // Rows from older backends omit access_role and always mean admin access.
    const accessRole = team.access_role ?? TEAM_ACCESS_ROLE.ADMIN;
    const existingGroup = groups.get(team.id);
    if (existingGroup) {
      existingGroup.accessRoles.add(accessRole);
      return;
    }
    groups.set(team.id, { team, accessRoles: new Set([accessRole]) });
  });
  return [...groups.values()];
};

/** Role badges for one team card — Admin and Teacher can both apply. */
export const getTeamRoleBadges = ({
  team,
  accessRoles,
  currentUserId
}: {
  team: Pick<SubscriptionTeam, 'owner_id' | 'is_school'>;
  accessRoles: Set<TEAM_ACCESS_ROLE>;
  currentUserId?: string;
}): SubscriptionRoleBadge[] => {
  const badges: SubscriptionRoleBadge[] = [];
  if (Boolean(currentUserId) && team.owner_id === currentUserId) {
    badges.push(createRoleBadge('owner'));
  } else if (accessRoles.has(TEAM_ACCESS_ROLE.ADMIN)) {
    badges.push(createRoleBadge('admin'));
  }
  if (accessRoles.has(TEAM_ACCESS_ROLE.TEACHER)) {
    badges.push(createRoleBadge('teacher'));
  }
  if (badges.length === 0) {
    badges.push(
      createRoleBadge(team.is_school === true ? 'student' : 'member')
    );
  }
  return badges;
};

/** Fallback badges when no team rows are available to attribute the plan to. */
export const getEntitlementRoleBadges = ({
  entitlements,
  isSchool
}: {
  entitlements: Entitlement[];
  isSchool: boolean;
}): SubscriptionRoleBadge[] => {
  const teamEntitlements = entitlements.filter((entitlement) =>
    TEAM_ACCESS_ENTITLEMENTS.includes(entitlement.entitlement)
  );
  if (teamEntitlements.length === 0) {
    return [];
  }
  // Real owners/admins always have admin-teams rows. Reaching this path with a
  // team_admin entitlement means the grant is stale — prefer member/student.
  return [createRoleBadge(isSchool ? 'student' : 'member')];
};

const ENTITLEMENT_PICK_ORDER: ENTITLEMENT[] = [
  ENTITLEMENT.TEAM_OWNER,
  ENTITLEMENT.TEAM_ADMIN,
  ENTITLEMENT.TEAM_MEMBER
];

const pickRepresentativeTeamSubscription = (
  teamSubscriptions: Subscription[],
  entitlements: Entitlement[]
): Subscription => {
  const entitlementByProduct = new Map(
    entitlements.map((entitlement) => [
      entitlement.product_identifier,
      entitlement.entitlement
    ])
  );
  for (const role of ENTITLEMENT_PICK_ORDER) {
    const match = teamSubscriptions.find(
      (subscription) =>
        entitlementByProduct.get(subscription.product_identifier) === role
    );
    if (match) {
      return match;
    }
  }
  return sortSubscriptionsByLatest(teamSubscriptions)[0];
};

const buildTeamCard = ({
  group,
  subscription,
  currentUserId
}: {
  group: TeamAccessGroup;
  subscription: Subscription;
  currentUserId?: string;
}): SubscriptionListItem => ({
  id: `team-${group.team.id}`,
  subscription,
  roleBadges: getTeamRoleBadges({
    team: group.team,
    accessRoles: group.accessRoles,
    currentUserId
  }),
  isTeamPlan: true,
  teamName: group.team.name,
  teamExpiryDate: group.team.expires_date ?? null,
  isSchoolTeam: group.team.is_school === true
});

/**
 * One card per team the user belongs to. Falls back to a single merged card
 * when team rows are unavailable (plain members, or teams not loaded yet).
 */
const buildTeamPlanItems = ({
  teamSubscriptions,
  entitlements,
  teams,
  currentUserId
}: {
  teamSubscriptions: Subscription[];
  entitlements: Entitlement[];
  teams: SubscriptionTeam[];
  currentUserId?: string;
}): SubscriptionListItem[] => {
  const subscription = pickRepresentativeTeamSubscription(
    teamSubscriptions,
    entitlements
  );
  const groups = groupTeamsById(teams);
  if (groups.length === 0) {
    return [
      {
        id: `team-access-${subscription.product_identifier}`,
        subscription,
        roleBadges: getEntitlementRoleBadges({ entitlements, isSchool: false }),
        isTeamPlan: true,
        teamName: null,
        teamExpiryDate: null,
        isSchoolTeam: false
      }
    ];
  }
  return groups.map((group) =>
    buildTeamCard({ group, subscription, currentUserId })
  );
};

const buildPlainItem = (
  subscription: Subscription,
  index: number
): SubscriptionListItem => ({
  id: [
    subscription.product_identifier,
    subscription.purchase_date ?? 'no_purchase',
    index
  ].join('_'),
  subscription,
  roleBadges: [],
  isTeamPlan: false,
  teamName: null,
  teamExpiryDate: null,
  isSchoolTeam: false
});

/**
 * Team/school products become one card per team; personal and other plans
 * stay as their own rows.
 */
export const buildSubscriptionListItems = ({
  subscriptions,
  entitlements,
  teams,
  currentUserId
}: {
  subscriptions: Subscription[];
  entitlements: Entitlement[];
  teams: SubscriptionTeam[];
  currentUserId?: string;
}): SubscriptionListItem[] => {
  const teamSubscriptions = subscriptions.filter((subscription) =>
    isTeamSubscriptionPlan(subscription.product_identifier)
  );
  const otherSubscriptions = subscriptions.filter(
    (subscription) => !isTeamSubscriptionPlan(subscription.product_identifier)
  );

  return [
    ...(teamSubscriptions.length > 0
      ? buildTeamPlanItems({
          teamSubscriptions,
          entitlements,
          teams,
          currentUserId
        })
      : []),
    ...otherSubscriptions.map(buildPlainItem)
  ];
};

export const isPersonalTrialOrPromoEntitlement = (
  entitlement?: Entitlement
): boolean => {
  if (!entitlement) {
    return false;
  }

  const isTeamEntitlement = [
    ENTITLEMENT.TEAM_MEMBER,
    ENTITLEMENT.TEAM_ADMIN,
    ENTITLEMENT.TEAM_OWNER
  ].includes(entitlement.entitlement);
  const productIdentifier = (
    entitlement.product_identifier ?? ''
  ).toLowerCase();
  const isTrialOrPromoIdentifier =
    productIdentifier.includes('trial') || productIdentifier.includes('promo');

  return !isTeamEntitlement && isTrialOrPromoIdentifier;
};

export const hasActiveTrialEntitlement = (
  entitlements: Entitlement[]
): boolean =>
  entitlements.length === SINGLE_ENTITLEMENT &&
  entitlements.some(
    (entitlement) =>
      entitlement.product_identifier?.includes('trial') &&
      Boolean(
        entitlement.expires_date && isFutureDateTime(entitlement.expires_date)
      )
  );

export interface TrialStatus {
  isTrialUser: boolean;
  isTrialActive: boolean;
  trialExpiresDate: string | null;
  trialDaysLeft: number | null;
}

export const getTrialStatus = (
  entitlements: Entitlement[],
  activeEntitlements: ActiveEntitlement[] = []
): TrialStatus => {
  const trialActiveEntitlement = activeEntitlements.find(
    ({ entitlement }) => entitlement === ENTITLEMENT.TRIAL
  );
  const trialEntitlement = entitlements.find(
    ({ entitlement }) => entitlement === ENTITLEMENT.TRIAL
  );

  const trialExpiresDate =
    trialActiveEntitlement?.expires_date ??
    trialEntitlement?.expires_date ??
    null;
  const isTrialActive =
    trialActiveEntitlement?.entitlement === ENTITLEMENT.TRIAL ||
    hasActiveTrialEntitlement([
      ...(trialEntitlement ? [trialEntitlement] : [])
    ]);
  const isTrialUser =
    isTrialActive ||
    entitlements.some(({ entitlement }) => entitlement === ENTITLEMENT.TRIAL);
  const trialDaysLeft =
    trialActiveEntitlement?.days_left ??
    getDaysUntilDate(trialEntitlement?.expires_date) ??
    null;

  return {
    isTrialUser,
    isTrialActive,
    trialExpiresDate,
    trialDaysLeft
  };
};

export const getSubscriptionPlanInfo = (planId?: string) => {
  if (planId === SUBSCRIPTION_PLAN.TEAM) {
    const terminology = getSubscriptionTerminology();
    return {
      title: t(terminology.planTitle),
      description: t(terminology.planDescription),
      features: [
        t(terminology.addRemoveUsers),
        t('subscription_page.project_management_integrations'),
        t(terminology.productiveYetPrivate),
        t(terminology.leaderBoard),
        t('subscription_page.team_school_feature')
      ],
      price: {
        amount: SUBSCRIPTION_PRICE.TEAM,
        postfix: t(terminology.planPostfix)
      },
      price_id: SUBSCRIPTION_PRICE_ID.TEAM
    };
  } else if (planId === SUBSCRIPTION_PLAN.ORGANIZATION_LICENSES) {
    return {
      title: 'Focus Bear organisation license for 5 years for 180 licenses',
      description:
        'Includes access to desktop and mobile apps for 180 licenses for 5 years',
      features: [],
      price: {
        amount: SUBSCRIPTION_PRICE.ORGANIZATION,
        postfix: t('subscription_page.team_plan_postfix')
      },
      price_id: SUBSCRIPTION_PRICE_ID.ORGANIZATION
    };
  } else if (planId === SUBSCRIPTION_PLAN.PERSONAL_ANNUAL) {
    return {
      title: t('subscription_page.personal_annual_subscription'),
      description: '',
      features: [],
      price: {
        amount: SUBSCRIPTION_PRICE.PERSONAL_ANNUAL,
        postfix: t('subscription_page.year')
      },
      price_id: SUBSCRIPTION_PRICE_ID.PERSONAL_ANNUAL
    };
  } else if (planId === SUBSCRIPTION_PLAN.LIFE_TIME) {
    return {
      title: t('subscription_page.lifetime_subscription'),
      description: t('subscription_page.own_it_for_life'),
      features: [],
      price: {
        amount: SUBSCRIPTION_PRICE.LIFE_TIME,
        postfix: t('subscription_page.lifetime')
      },
      price_id: SUBSCRIPTION_PRICE_ID.LIFE_TIME
    };
  }
  return {
    title: t('subscription_page.personal_subscription'),
    description: t('subscription_page.personal_description'),
    features: [
      t('subscription_page.unlimited_ai_assisted_focus_modes'),
      t('subscription_page.unlimited_pomodoro_modes'),
      t('subscription_page.unlimited_meeting_modes'),
      t('subscription_page.unlimited_automatic_goal_tracking'),
      t('subscription_page.ai_powered_distraction_blocking'),
      t('subscription_page.unlimited_habits'),
      t('subscription_page.cross_device_sync')
    ],
    price: {
      amount: SUBSCRIPTION_PRICE.PERSONAL_MONTHLY,
      postfix: t('subscription_page.personal_plan_postfix')
    },
    price_id: SUBSCRIPTION_PRICE_ID.PERSONAL_MONTHLY
  };
};
